import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FieldType,
  PersonStatus,
  type FieldDefinitionDto,
  type FieldsSchemaDto,
  type PersonDetailDto,
  type PersonFieldHistoryDto,
  type SetFieldValueInput,
} from '@ciimar/shared';
import { api } from '../api/client';
import { Combobox } from '../components/Combobox';
import { useAuth } from '../auth/AuthContext';
import { useLocalizedLabel, useT, type Translate } from '../lib/i18n';
import { setTabTitle, firstLastName } from '../lib/tab-titles';

type Draft = Record<string, unknown>;

/** A value counts as "filled" unless it is nullish, an empty string, or [] (false/0 count). */
function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

const CORE_FIELDS: { key: keyof PersonDetailDto; labelKey: string; type?: string }[] = [
  { key: 'code', labelKey: 'people.record.field.code' },
  { key: 'fullName', labelKey: 'people.record.field.fullName' },
  { key: 'shortName', labelKey: 'people.record.field.shortName' },
  { key: 'personalEmail', labelKey: 'people.record.field.personalEmail', type: 'email' },
  { key: 'professionalEmail', labelKey: 'people.record.field.professionalEmail', type: 'email' },
  { key: 'orcid', labelKey: 'people.record.field.orcid' },
  { key: 'cienciaId', labelKey: 'people.record.field.cienciaId' },
  { key: 'scopusAuthorId', labelKey: 'people.record.field.scopusAuthorId' },
];

/** A single pending change, shown in the "unsaved changes" confirmation. */
interface Change {
  label: string;
  from: string;
  to: string;
}

function displayValue(t: Translate, type: FieldType | 'core' | 'status', v: unknown): string {
  if (type === 'status')
    return v === PersonStatus.Inactive ? t('shared.inactive') : t('shared.active');
  if (v === undefined || v === null || v === '') return t('people.record.empty');
  if (typeof v === 'boolean') return v ? t('common.yes') : t('common.no');
  if (Array.isArray(v)) return v.length ? v.join(', ') : t('people.record.empty');
  return String(v);
}

/** Fields whose value is a collaborator code — edited via a person picker that
 *  shows the name (e.g. the validation chain on the Portal de Colaborador). */
const PERSON_FIELD_KEYS = new Set(['primeiro_nivel_validacao', 'segundo_nivel_validacao']);

export function PersonRecord({
  personId,
  selfMode = false,
}: {
  /** When set, shows this person's record instead of the `:id` route param (used by "Meu Perfil"). */
  personId?: string;
  /** Self view ("Meu Perfil"): hides the Colaboradores back link. */
  selfMode?: boolean;
} = {}) {
  const params = useParams<{ id: string }>();
  const id = personId ?? params.id;
  const { isManager } = useAuth();
  const t = useT();
  const loc = useLocalizedLabel();
  const navigate = useNavigate();
  const [person, setPerson] = useState<PersonDetailDto | null>(null);
  const [schema, setSchema] = useState<FieldsSchemaDto | null>(null);
  const [active, setActive] = useState('cadastro');
  const [core, setCore] = useState<Draft>({});
  const [values, setValues] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [viewReserved, setViewReserved] = useState(false);
  // A pending navigation held back while there are unsaved changes.
  const [pending, setPending] = useState<{ run: () => void } | null>(null);
  // Dropdown options for reference (coded) fields, by definition id.
  const [refOptions, setRefOptions] = useState<Record<string, string[]>>({});
  // Collaborators as { value: code, label: name } for the person-picker fields.
  const [people, setPeople] = useState<{ value: string; label: string }[]>([]);
  // Field-change history (loaded on demand into a modal).
  const [history, setHistory] = useState<PersonFieldHistoryDto[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  async function openHistory() {
    if (!id) return;
    setHistoryOpen(true);
    if (history === null) {
      try {
        setHistory(await api.personHistory(id));
      } catch {
        setHistory([]);
      }
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [p, s, perms, opts, roster] = await Promise.all([
        api.getPerson(id),
        api.fieldsSchema(),
        api.myPermissions(),
        api.personFieldOptions().catch(() => ({}) as Record<string, string[]>),
        api.listPeople().catch(() => []),
      ]);
      hydrate(p);
      setSchema(s);
      setViewReserved(perms.viewReserved);
      setRefOptions(opts);
      setPeople(
        roster.filter((r) => r.code).map((r) => ({ value: r.code as string, label: r.fullName })),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function hydrate(p: PersonDetailDto) {
    setPerson(p);
    // Label this collaborator's browser tab with their name (first + last).
    if (!selfMode && id) setTabTitle(`/people/${id}`, firstLastName(p.fullName));
    setCore({
      code: p.code ?? '',
      fullName: p.fullName,
      shortName: p.shortName ?? '',
      personalEmail: p.personalEmail ?? '',
      professionalEmail: p.professionalEmail ?? '',
      status: p.status,
      orcid: p.orcid ?? '',
      cienciaId: p.cienciaId ?? '',
      scopusAuthorId: p.scopusAuthorId ?? '',
    });
    const v: Draft = {};
    for (const fv of p.values) v[fv.definitionId] = fv.value;
    setValues(v);
    setSaved(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sections = schema?.sections ?? [];
  const activeSection = useMemo(() => sections.find((s) => s.key === active), [sections, active]);
  const defById = useMemo(() => {
    const m = new Map<string, FieldDefinitionDto>();
    for (const s of sections) for (const d of s.fields) m.set(d.id, d);
    return m;
  }, [sections]);
  const defIdByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sections) for (const d of s.fields) m.set(d.key, d.id);
    return m;
  }, [sections]);

  /** Applies a field value, with cross-field cascades (e.g. picking a contract
   *  pulls its reference into "Referência do Contrato"). */
  function onFieldChange(def: FieldDefinitionDto, v: unknown) {
    setValues((prev) => {
      const next = { ...prev, [def.id]: v };
      if (def.key === 'contrato') {
        const refId = defIdByKey.get('referencia_contrato');
        if (refId) next[refId] = v ?? '';
      }
      return next;
    });
  }

  // The set of unsaved changes (for the Save button, the guard and the summary).
  const changes = useMemo<Change[]>(() => {
    if (!person) return [];
    const out: Change[] = [];
    for (const f of CORE_FIELDS) {
      const cur = (core[f.key as string] ?? '') as string;
      const orig = (person[f.key] ?? '') as string;
      if (String(cur) !== String(orig)) {
        out.push({
          label: t(f.labelKey),
          from: displayValue(t, 'core', orig),
          to: displayValue(t, 'core', cur),
        });
      }
    }
    if (core.status !== person.status) {
      out.push({
        label: t('people.record.field.situacao'),
        from: displayValue(t, 'status', person.status),
        to: displayValue(t, 'status', core.status),
      });
    }
    const original: Draft = {};
    for (const fv of person.values) original[fv.definitionId] = fv.value;
    const ids = new Set([...Object.keys(values), ...Object.keys(original)]);
    for (const defId of ids) {
      const cur = values[defId];
      const orig = original[defId];
      if (JSON.stringify(cur ?? null) !== JSON.stringify(orig ?? null)) {
        const def = defById.get(defId);
        out.push({
          label: def ? loc(def) : defId,
          from: displayValue(t, def?.type ?? FieldType.Text, orig),
          to: displayValue(t, def?.type ?? FieldType.Text, cur),
        });
      }
    }
    return out;
  }, [person, core, values, defById, t, loc]);

  const dirty = changes.length > 0;

  // Warn on a browser close/refresh/navigation while there are unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  /** Run a navigation now, or hold it behind the unsaved-changes dialog. */
  function guard(run: () => void) {
    if (dirty) setPending({ run });
    else run();
  }
  function requestTab(key: string) {
    if (key === active) return;
    guard(() => setActive(key));
  }

  async function save(): Promise<boolean> {
    if (!id || !person) return false;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const corePatch: Record<string, unknown> = {};
      for (const f of CORE_FIELDS) {
        const cur = (core[f.key as string] ?? '') as string;
        if (cur !== (person[f.key] ?? '')) corePatch[f.key as string] = cur;
      }
      if (core.status !== person.status) corePatch.status = core.status;

      const original: Draft = {};
      for (const fv of person.values) original[fv.definitionId] = fv.value;
      const valuePatch: SetFieldValueInput[] = [];
      for (const [definitionId, value] of Object.entries(values)) {
        if (JSON.stringify(value ?? null) !== JSON.stringify(original[definitionId] ?? null)) {
          valuePatch.push({ definitionId, value });
        }
      }

      let updated = person;
      if (Object.keys(corePatch).length > 0) updated = await api.updatePerson(id, corePatch);
      if (valuePatch.length > 0) updated = await api.setPersonValues(id, valuePatch);
      hydrate(updated);
      setSaved(true);
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Deletion is a manager action, allowed only within 72h of creation.
  const createdMs = person?.createdAt ? new Date(person.createdAt).getTime() : 0;
  const canDelete =
    isManager && !selfMode && createdMs > 0 && Date.now() - createdMs <= 72 * 60 * 60 * 1000;

  async function remove() {
    if (!id || !person) return;
    const ok = window.confirm(t('people.record.deleteConfirm', { name: person.fullName }));
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.deletePerson(id);
      navigate('/people');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  // Live completeness: required fields the viewer can see that have no value.
  const missingRequired = useMemo(() => {
    const out: { id: string; label: string; sectionKey: string }[] = [];
    for (const s of sections) {
      for (const def of s.fields) {
        if (!def.required) continue;
        if (!viewReserved && (def.reserved || s.reserved)) continue;
        if (isEmptyValue(values[def.id]))
          out.push({ id: def.id, label: loc(def), sectionKey: s.key });
      }
    }
    return out;
  }, [sections, values, viewReserved, loc]);

  if (loading) return <p className="muted">{t('common.loading')}</p>;
  if (!person) return <p className="alert error">{error ?? t('people.record.notFound')}</p>;

  return (
    <section className="detail">
      {!selfMode && (
        <Link
          to="/people"
          className="back"
          onClick={(e) => {
            if (dirty) {
              e.preventDefault();
              guard(() => navigate('/people'));
            }
          }}
        >
          {t('people.record.back')}
        </Link>
      )}
      <div className="page-head record-head">
        {id && (
          <img
            className="record-photo"
            src={api.personPhotoUrl(id)}
            alt=""
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <h2>{person.fullName}</h2>
        <div className="spacer" />
        <button type="button" className="btn-ghost" onClick={() => void openHistory()}>
          {t('people.record.history')}
        </button>
        {canDelete && (
          <button
            type="button"
            className="btn-danger"
            onClick={() => void remove()}
            disabled={busy}
            title={t('people.record.deleteTitle')}
          >
            {t('common.delete')}
          </button>
        )}
        {isManager && dirty && (
          <button type="button" onClick={() => void save()} disabled={busy}>
            {busy ? t('people.record.saving') : t('common.save')}
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {saved && <div className="alert ok">{t('people.record.saved')}</div>}
      {missingRequired.length > 0 && (
        <div className="alert warn">
          {t('people.record.missingRequired', { n: missingRequired.length })}{' '}
          {missingRequired.map((m, i) => (
            <span key={m.id}>
              {i > 0 && ', '}
              <button
                type="button"
                className="link-button"
                onClick={() => requestTab(m.sectionKey)}
              >
                {m.label}
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="tabs">
        {sections.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`tab${s.key === active ? ' active' : ''}`}
            onClick={() => requestTab(s.key)}
          >
            {loc(s)}
          </button>
        ))}
      </div>

      {active === 'cadastro' ? (
        <div className="field-grid">
          {CORE_FIELDS.map((f) => (
            <label key={f.key as string}>
              {t(f.labelKey)}
              <input
                type={f.type ?? 'text'}
                value={(core[f.key as string] as string) ?? ''}
                title={(core[f.key as string] as string) || undefined}
                readOnly={!isManager}
                onChange={(e) => setCore({ ...core, [f.key as string]: e.target.value })}
              />
            </label>
          ))}
          <label>
            {t('people.record.field.situacao')}
            <BoolLikeStatus
              value={core.status as string}
              readOnly={!isManager}
              onChange={(v) => setCore({ ...core, status: v })}
            />
          </label>
        </div>
      ) : (
        <div className="field-grid">
          {activeSection?.fields
            .filter((def) => viewReserved || !(def.reserved || activeSection.reserved))
            .map((def) => (
              <FieldControl
                key={def.id}
                def={def}
                sectionReserved={activeSection.reserved}
                value={values[def.id]}
                refOptions={refOptions[def.id]}
                personOptions={people}
                readOnly={!isManager}
                onChange={(v) => onFieldChange(def, v)}
              />
            ))}
          {activeSection && activeSection.fields.length === 0 && (
            <p className="muted">{t('people.record.emptySection')}</p>
          )}
        </div>
      )}

      {pending && (
        <ConfirmLeave
          changes={changes}
          busy={busy}
          onCancel={() => setPending(null)}
          onDiscard={() => {
            hydrate(person);
            const run = pending.run;
            setPending(null);
            run();
          }}
          onSave={async () => {
            const run = pending.run;
            const ok = await save();
            if (ok) {
              setPending(null);
              run();
            }
          }}
        />
      )}

      {historyOpen && (
        <HistoryModal
          rows={history}
          onClose={() => setHistoryOpen(false)}
          formatDate={formatHistoryDate}
        />
      )}
    </section>
  );
}

/** A collaborator's field-change history (previous value → new value, date). */
function HistoryModal({
  rows,
  onClose,
  formatDate,
}: {
  rows: PersonFieldHistoryDto[] | null;
  onClose: () => void;
  formatDate: (iso: string) => string;
}) {
  const t = useT();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{t('people.record.hist.title')}</h3>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        {rows === null ? (
          <p className="muted">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="muted">{t('people.record.hist.noHistory')}</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>{t('people.record.hist.date')}</th>
                <th>{t('people.record.hist.field')}</th>
                <th>{t('people.record.hist.from')}</th>
                <th>{t('people.record.hist.to')}</th>
                <th>{t('people.record.hist.source')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id}>
                  <td>{formatDate(h.changedAt)}</td>
                  <td>{h.fieldLabel}</td>
                  <td className="muted">{h.oldValue ?? '—'}</td>
                  <td>{h.newValue ?? '—'}</td>
                  <td>
                    {h.source === 'sistrade-sync' ? 'SISTRADE' : t('people.record.hist.manual')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-PT');
}

/** Situação as a Sim/Não-style segmented control (Ativo / Inativo). */
function BoolLikeStatus({
  value,
  readOnly,
  onChange,
}: {
  value: string;
  readOnly: boolean;
  onChange: (v: string) => void;
}) {
  const t = useT();
  return (
    <div className="seg" role="group" aria-label={t('people.record.statusAria')}>
      <button
        type="button"
        className={`seg-btn${value === PersonStatus.Active ? ' on' : ''}`}
        disabled={readOnly}
        onClick={() => onChange(PersonStatus.Active)}
      >
        {t('shared.active')}
      </button>
      <button
        type="button"
        className={`seg-btn${value === PersonStatus.Inactive ? ' on danger' : ''}`}
        disabled={readOnly}
        onClick={() => onChange(PersonStatus.Inactive)}
      >
        {t('shared.inactive')}
      </button>
    </div>
  );
}

/** Sim/Não segmented control; nothing is highlighted until a choice is made. */
function BoolField({
  value,
  readOnly,
  onChange,
}: {
  value: unknown;
  readOnly: boolean;
  onChange: (v: boolean) => void;
}) {
  const t = useT();
  return (
    <div className="seg" role="group">
      <button
        type="button"
        className={`seg-btn${value === true ? ' on' : ''}`}
        disabled={readOnly}
        onClick={() => onChange(true)}
      >
        {t('common.yes')}
      </button>
      <button
        type="button"
        className={`seg-btn${value === false ? ' on' : ''}`}
        disabled={readOnly}
        onClick={() => onChange(false)}
      >
        {t('common.no')}
      </button>
    </div>
  );
}

/**
 * Date/DateTime input that stays a plain empty box until focused, so an unset
 * date never shows today's date as if it were a value.
 */
function DateField({
  value,
  dateTime,
  readOnly,
  onChange,
}: {
  value: unknown;
  dateTime?: boolean;
  readOnly: boolean;
  onChange: (v: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const str = dateTime ? ((value as string) ?? '').slice(0, 16) : ((value as string) ?? '');
  const asDate = focused || str !== '';
  return (
    <input
      type={asDate ? (dateTime ? 'datetime-local' : 'date') : 'text'}
      placeholder="—"
      value={str}
      readOnly={readOnly}
      disabled={readOnly}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FieldControl({
  def,
  value,
  readOnly,
  sectionReserved,
  refOptions,
  personOptions,
  onChange,
}: {
  def: FieldDefinitionDto;
  value: unknown;
  readOnly: boolean;
  sectionReserved?: boolean;
  refOptions?: string[];
  personOptions?: { value: string; label: string }[];
  onChange: (v: unknown) => void;
}) {
  const t = useT();
  const loc = useLocalizedLabel();
  const common = { readOnly, disabled: readOnly };
  let control: React.ReactNode;
  if (PERSON_FIELD_KEYS.has(def.key)) {
    // Person picker: the stored value is a collaborator code; show the name and
    // let the user search by name (any word order, accent-insensitive).
    const cur = (value as string) ?? '';
    const opts = personOptions ?? [];
    // Keep the current code selectable even if it is not in the loaded roster.
    const list =
      cur && !opts.some((o) => o.value === cur) ? [{ value: cur, label: cur }, ...opts] : opts;
    control = (
      <Combobox value={cur} options={list} disabled={readOnly} onChange={(v) => onChange(v)} />
    );
  } else if (def.reference) {
    // Coded field: pick from the existing reference values (dropdown). The
    // current value is always kept as an option so nothing is lost.
    const cur = (value as string) ?? '';
    const opts = refOptions ?? [];
    const list = cur && !opts.includes(cur) ? [cur, ...opts] : opts;
    control = (
      <Combobox
        value={cur}
        options={list.map((o) => ({ value: o, label: o }))}
        disabled={readOnly}
        onChange={(v) => onChange(v)}
      />
    );
  } else {
    switch (def.type) {
      case FieldType.LongText:
        control = (
          <textarea
            rows={4}
            value={(value as string) ?? ''}
            {...common}
            onChange={(e) => onChange(e.target.value)}
          />
        );
        break;
      case FieldType.Boolean:
        control = <BoolField value={value} readOnly={readOnly} onChange={onChange} />;
        break;
      case FieldType.Select:
        control = (
          <Combobox
            value={(value as string) ?? ''}
            options={def.options.map((o) => ({ value: o.value, label: loc(o) }))}
            disabled={readOnly}
            onChange={(v) => onChange(v)}
          />
        );
        break;
      case FieldType.MultiSelect: {
        const arr = Array.isArray(value) ? (value as string[]) : [];
        control = (
          <span className="checks">
            {def.options.map((o) => (
              <label key={o.value} className="check">
                <input
                  type="checkbox"
                  checked={arr.includes(o.value)}
                  disabled={readOnly}
                  onChange={(e) =>
                    onChange(
                      e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value),
                    )
                  }
                />
                {loc(o)}
              </label>
            ))}
          </span>
        );
        break;
      }
      case FieldType.Date:
        control = <DateField value={value} readOnly={readOnly} onChange={onChange} />;
        break;
      case FieldType.DateTime:
        control = <DateField value={value} dateTime readOnly={readOnly} onChange={onChange} />;
        break;
      case FieldType.Number:
      case FieldType.Integer:
      case FieldType.Currency:
      case FieldType.Percent:
        control = (
          <input
            type="number"
            step={def.type === FieldType.Integer ? '1' : 'any'}
            value={(value as string | number) ?? ''}
            {...common}
            onChange={(e) => onChange(e.target.value)}
          />
        );
        break;
      default:
        control = (
          <input
            type={
              def.type === FieldType.Email ? 'email' : def.type === FieldType.Url ? 'url' : 'text'
            }
            value={(value as string) ?? ''}
            title={(value as string) || undefined}
            {...common}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  }
  return (
    <label>
      {loc(def)}
      {def.required && <span className="req"> *</span>}
      {(def.reserved || sectionReserved) && (
        <span className="reserved-tag">{t('people.record.reserved')}</span>
      )}
      {control}
    </label>
  );
}

/** Unsaved-changes dialog: lists each change (from → to) before leaving. */
function ConfirmLeave({
  changes,
  busy,
  onSave,
  onDiscard,
  onCancel,
}: {
  changes: Change[];
  busy: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>{t('people.record.confirmLeave.title')}</h3>
        <p className="muted">{t('people.record.confirmLeave.body')}</p>
        <ul className="change-list">
          {changes.map((c, i) => (
            <li key={i}>
              <strong>{c.label}:</strong> <span className="muted">{c.from}</span> → {c.to}
            </li>
          ))}
        </ul>
        <div className="actions-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="link-button" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn-ghost" onClick={onDiscard} disabled={busy}>
            {t('people.record.confirmLeave.discard')}
          </button>
          <button type="button" onClick={onSave} disabled={busy}>
            {busy ? t('people.record.saving') : t('people.record.confirmLeave.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
